// Chat Restoration Binder - Rebuilds controller/tool-owned chat rows from saved chat data.

import {
  assistantTextBubbleController,
  assistantThinkingBubbleController,
  assistantToolBubbleController,
  userMessageBubbleController,
} from "../controllers/index.js";

function parseTimestamp(timestamp) {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;

  const isoDate = new Date(timestamp);
  if (!Number.isNaN(isoDate.getTime())) return isoDate;

  const formattedMatch = String(timestamp).match(
    /(\d{1,2})\/(\d{1,2})\/(\d{4}), (\d{1,2}):(\d{2}):(\d{2})/
  );
  if (formattedMatch) {
    const [, month, day, year, hour, minute, second] = formattedMatch;
    return new Date(year, month - 1, day, hour, minute, second);
  }

  return new Date();
}

function parseToolCallArguments(args) {
  if (!args) return {};
  if (typeof args === "string") {
    try {
      return JSON.parse(args);
    } catch {
      return {};
    }
  }
  if (typeof args === "object") return args;
  return {};
}

function createToolBubbleConfig(props = {}) {
  return assistantToolBubbleController.createToolBubble(props);
}

function reconstructUserMessageBubbles({
  message,
  messageId,
  chatArea,
  onLinkUserMessageBubbles,
}) {
  const segments = message.data?.segments || [];
  const imageSegments = segments.filter((segment) => segment.type === "image");
  const fileSegments = segments.filter((segment) => segment.type === "file");
  const textSegments = segments.filter((segment) => segment.type === "text");

  const hasSegments =
    imageSegments.length > 0 || fileSegments.length > 0 || textSegments.length > 0;
  const fallbackText =
    !hasSegments && message.data?.content ? String(message.data.content) : "";
  const timestamp = parseTimestamp(message.data?.timestamp);

  const bubbleConfigs = userMessageBubbleController.createMessageBubbles({
    text: textSegments.map((segment) => segment.content).join(" ") || fallbackText,
    images: imageSegments.map((segment) => segment.content),
    files: fileSegments.map((segment) => segment.content),
    fileNames: fileSegments.map((segment) => segment.fileName),
    timestamp,
    messageId,
  });

  const bubbleIds = [];
  let lastBubbleId = null;
  bubbleConfigs.forEach((bubbleConfig) => {
    const bubbleId = chatArea.createBubble({ ...bubbleConfig, messageId });
    if (bubbleId) {
      bubbleIds.push(bubbleId);
      lastBubbleId = bubbleId;
    }
  });

  if (onLinkUserMessageBubbles && bubbleIds.length > 0) {
    onLinkUserMessageBubbles({ messageId, bubbleIds, lastBubbleId });
  }
}

function reconstructAssistantMessageBubbles({
  message,
  messageId,
  chatArea,
  aiToolSystem,
  getBubbleStyle: getBubbleStyleOverride,
}) {
  const resolveBubbleStyle = (functionName) => {
    if (typeof getBubbleStyleOverride === "function") {
      return getBubbleStyleOverride(functionName);
    }
    return aiToolSystem?.getToolInstance?.(functionName, null)?.bubbleStyle || "simple";
  };

  const createToolUiActionHandler = (functionName, toolCallId) => {
    return (actionId, payload, context = {}) => {
      const toolInstance = aiToolSystem?.getToolInstance?.(functionName, null);
      if (!toolInstance || typeof toolInstance.handleToolUiAction !== "function") {
        return false;
      }
      return toolInstance.handleToolUiAction(actionId, payload, {
        ...context,
        functionName,
        toolCallId,
        aiToolSystem,
      });
    };
  };

  const segments = message.data?.segments || [];

  if (segments.length > 0) {
    for (const segment of segments) {
      if (segment.type === "tool_call") {
        const functionName =
          segment.functionName || segment.content?.functionName || "Unknown Function";
        const argumentsData = parseToolCallArguments(
          segment.arguments || segment.content?.arguments
        );
        const toolCallId =
          segment.toolCallId ||
          segment.content?.tool_call_id ||
          segment.content?.toolCallId ||
          segment.content?.id;
        const toolInstance = aiToolSystem?.getToolInstance?.(functionName, null);
        const toolUiHtml =
          typeof segment.toolUiHtml === "string" && segment.toolUiHtml.length > 0
            ? segment.toolUiHtml
            : typeof toolInstance?.renderHistoricalToolBubbleUi === "function"
              ? toolInstance.renderHistoricalToolBubbleUi({
                  toolCallId,
                  functionName,
                  args: argumentsData,
                  status: segment.toolStatus || "completed",
                }) || ""
              : "";

        chatArea.createBubble(
          createToolBubbleConfig({
            content: {
              tool_calls: [
                {
                  function: {
                    name: functionName,
                    arguments: argumentsData,
                  },
                },
              ],
            },
            functionName,
            messageId,
            toolCallId,
            showTimestamp: false,
            timestamp: parseTimestamp(segment.startTimestamp || message.data?.timestamp),
            status: segment.toolStatus || "completed",
            bubbleStyle: segment.bubbleStyle || resolveBubbleStyle(functionName),
            toolUiHtml,
            onToolUiAction: createToolUiActionHandler(functionName, toolCallId),
          })
        );
        continue;
      }

      if (segment.type === "text") {
        chatArea.createBubble(
          assistantTextBubbleController.createBubble({
            content: segment.content || "",
            messageId,
            showTimestamp: true,
            timestamp: parseTimestamp(segment.endTimestamp || segment.startTimestamp),
            isComplete: true,
          })
        );
        continue;
      }

      if (segment.type === "reasoning") {
        chatArea.createBubble(
          assistantThinkingBubbleController.createBubble({
            content: segment.content || "",
            messageId,
            showTimestamp: true,
            timestamp: parseTimestamp(segment.endTimestamp || segment.startTimestamp),
            startTimestamp: parseTimestamp(segment.startTimestamp),
            endTimestamp: parseTimestamp(segment.endTimestamp || segment.startTimestamp),
            isDurationActive: false,
            isExpanded: segment.isExpanded !== false,
            durationDisplayMode: segment.durationDisplayMode || "formatted",
            isComplete: true,
          })
        );
      }
    }
    return;
  }

  const toolCalls = message.data?.tool_calls;
  if (toolCalls && toolCalls.length > 0) {
    for (const toolCall of toolCalls) {
      const functionName = toolCall.function?.name || "Unknown Function";
      const argumentsData = parseToolCallArguments(toolCall.function?.arguments);
      const toolInstance = aiToolSystem?.getToolInstance?.(functionName, null);
      const toolUiHtml =
        typeof toolInstance?.renderHistoricalToolBubbleUi === "function"
          ? toolInstance.renderHistoricalToolBubbleUi({
              toolCallId: toolCall.id,
              functionName,
              args: argumentsData,
              status: "completed",
            }) || ""
          : "";

      chatArea.createBubble(
        createToolBubbleConfig({
          content: {
            tool_calls: [
              {
                ...toolCall,
                function: {
                  name: functionName,
                  arguments: argumentsData,
                },
              },
            ],
          },
          functionName,
          messageId,
          toolCallId: toolCall.id,
          showTimestamp: false,
          timestamp: parseTimestamp(message.data?.timestamp),
          status: "completed",
          bubbleStyle: resolveBubbleStyle(functionName),
          toolUiHtml,
          onToolUiAction: createToolUiActionHandler(functionName, toolCall.id),
        })
      );
    }
  }
}

export function reconstructMessageBubbles({
  message,
  messageId,
  chatArea,
  aiToolSystem,
  onLinkUserMessageBubbles,
  getBubbleStyle,
}) {
  const role = message.data?.role;

  if (role === "user") {
    reconstructUserMessageBubbles({
      message,
      messageId,
      chatArea,
      onLinkUserMessageBubbles,
    });
  } else if (role === "assistant") {
    reconstructAssistantMessageBubbles({
      message,
      messageId,
      chatArea,
      aiToolSystem,
      getBubbleStyle,
    });
  }
}

export function reconstructChatBubbles({
  chat,
  chatArea,
  aiToolSystem,
  options = {},
}) {
  if (!chatArea?.createBubble) {
    return;
  }

  const {
    onLinkUserMessageBubbles,
    clearFirst = true,
    getBubbleStyle: getBubbleStyleOverride,
  } = options;

  if (clearFirst && chatArea.clearBubbles) {
    chatArea.clearBubbles();
  }

  const messageIds = Array.isArray(chat.messageIDs) ? chat.messageIDs : [];
  const messages = chat.messages;

  if (messageIds.length > 0) {
    for (const messageId of messageIds) {
      const message = messages?.get?.(messageId) ?? chat.getMessage?.(messageId);
      if (!message) {
        continue;
      }
      reconstructMessageBubbles({
        message,
        messageId,
        chatArea,
        aiToolSystem,
        onLinkUserMessageBubbles,
        getBubbleStyle: getBubbleStyleOverride,
      });
    }
    return;
  }

  const entries = Array.from(messages?.entries?.() || []);
  for (const [messageId, message] of entries) {
    if (!message) {
      continue;
    }
    reconstructMessageBubbles({
      message,
      messageId,
      chatArea,
      aiToolSystem,
      onLinkUserMessageBubbles,
      getBubbleStyle: getBubbleStyleOverride,
    });
  }
}
